import { axiosApiInstance } from "./axios";
import { baseApiPath, get, post } from "./base-api";
import { store } from "../store";
import {
  IChatAttachment,
  IChatConversationResponse,
  IChatMessage,
  IChatPollResponse,
  ISendChatMessagePayload,
} from "../data-access";

// The shared axios instance rewrites every request header to
// "Content-Type: application/x-www-form-urlencoded", which destroys the
// multipart boundary on an upload. The upload therefore goes through fetch
// with the token read from the store, the same place the axios interceptor
// reads it from. Everything else, the binary download included, stays on axios
// so it keeps the 401 refresh-and-retry.
const getAuthHeaders = (): Record<string, string> => {
  const accessToken = store.getState()?.auth?.access_token;

  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

const rejectFromResponse = async (response: Response): Promise<never> => {
  const payload = await response.json().catch(() => null);

  return Promise.reject({
    status: response.status,
    text: payload?.message,
    reason: payload?.error,
    ...(payload?.errors && { details: payload.errors }),
  });
};

const getConversation = (
  before?: string
): Promise<IChatConversationResponse> => {
  const query = before ? `?before=${encodeURIComponent(before)}` : "";

  return get<IChatConversationResponse>(
    `${baseApiPath}/api/chat/conversation${query}`
  );
};

const sendMessage = (
  payload: ISendChatMessagePayload
): Promise<IChatMessage> => {
  return post<ISendChatMessagePayload, IChatMessage>(
    `${baseApiPath}/api/chat/messages`,
    payload
  );
};

const markAsRead = (): Promise<void> => {
  return post<Record<string, never>, void>(
    `${baseApiPath}/api/chat/read`,
    {} as Record<string, never>
  );
};

const poll = (): Promise<IChatPollResponse> => {
  return get<IChatPollResponse>(`${baseApiPath}/api/chat/poll`);
};

const uploadAttachment = async (file: File): Promise<IChatAttachment> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${baseApiPath}/api/chat/attachments`, {
    method: "POST",
    headers: { Accept: "application/json", ...getAuthHeaders() },
    body: formData,
  });

  if (!response.ok) {
    return rejectFromResponse(response);
  }

  return (await response.json()) as IChatAttachment;
};

// Attachments are served from the private disk behind an authorisation check,
// so a bare <img src> gets a 401. Fetch the bytes and hand the caller a blob.
// This runs through axios so an expired access token is refreshed and the
// request retried, exactly like every other call in the app.
const getAttachmentBlob = (
  attachmentId: string,
  signal?: AbortSignal
): Promise<Blob> => {
  return axiosApiInstance
    .get(`${baseApiPath}/api/chat/attachments/${attachmentId}`, {
      responseType: "blob",
      signal,
    })
    .then((response) => response.data as Blob);
};

export const chatApi = {
  getConversation,
  sendMessage,
  markAsRead,
  poll,
  uploadAttachment,
  getAttachmentBlob,
};
